import { useCallback, useEffect, useRef } from 'react';
import { usePostHog } from '@posthog/react';
import { ANALYTICS_EVENTS } from './events.js';
import { isAnalyticsGranted } from './consent/store.js';
import { useCookieConsent } from './consent/useCookieConsent.js';
import { useConsentMode, usePostHogReady } from './provider.js';
/**
 * Whether tracking may run now (BR-1): always under an exempt provider (admin, NG-6), otherwise
 * only once consent is resolved to granted. Also waits for posthog.init to have run (child effects
 * run before the provider's init effect). Re-renders when the decision or readiness changes.
 */
function useTrackingAllowed() {
    const consentMode = useConsentMode();
    const consent = useCookieConsent();
    const ready = usePostHogReady();
    return ready && (consentMode === 'exempt' || (consent.status === 'resolved' && consent.effective === 'granted'));
}
/**
 * THE consent-gated capture (BR-1): sends only under an exempt provider (admin, NG-6) or once
 * analytics consent is granted, and never throws into user code. Every event capture in this package
 * goes through here (enforced by no-direct-vendor-calls.test.ts).
 */
export function captureGated(posthog, consentMode, event, properties) {
    if (consentMode !== 'exempt' && !isAnalyticsGranted())
        return;
    try {
        posthog?.capture(event, properties);
    }
    catch {
        // analytics must never throw into user code
    }
}
export function useIdentifyUser(userId, role, app) {
    const posthog = usePostHog();
    const allowed = useTrackingAllowed();
    useEffect(() => {
        if (!posthog || !allowed)
            return;
        if (userId) {
            posthog.identify(userId, { role, app });
        }
        else {
            posthog.reset();
        }
    }, [posthog, allowed, userId, role, app]);
}
export function useTrackEvent() {
    const posthog = usePostHog();
    const consentMode = useConsentMode();
    return useCallback((event, properties) => {
        captureGated(posthog, consentMode, event, properties);
        // Test-only observability hook (mirrors error-boundary.tsx's identical pattern for
        // ERROR_SHOWN) — every web app disables AnalyticsProvider outside production
        // (`enabled={import.meta.env.PROD}`, Business Rule 5) and ships no VITE_POSTHOG_KEY in
        // dev/test builds, so `posthog` above is the uninitialised posthog-js global there and the
        // real capture() call is a no-op — this is the only way an e2e test can observe what
        // track() would have sent (e.g. ERR-3's payment_failed). It records regardless of the
        // consent decision. Complete no-op unless a test has explicitly installed the hook via
        // `page.addInitScript` before navigation; never present in production.
        if (typeof window !== 'undefined') {
            window.__RESIDA_E2E_ANALYTICS__?.record(event, properties);
        }
    }, [posthog, consentMode]);
}
export function useTrackFeature(featureName, app) {
    const posthog = usePostHog();
    const allowed = useTrackingAllowed();
    const tracked = useRef(false);
    useEffect(() => {
        if (!posthog || !allowed || tracked.current)
            return;
        tracked.current = true;
        try {
            posthog.capture(ANALYTICS_EVENTS.FEATURE_USED, {
                feature: featureName,
                app,
            });
        }
        catch {
            // analytics must never throw
        }
    }, [posthog, allowed, featureName, app]);
}
//# sourceMappingURL=hooks.js.map